import React, { FC, useEffect, useState } from "react";
import moment from "moment-timezone";
import styles from "./TimezoneInput.module.css";

interface TimezoneInputProps {
    label?: string;
    value: string | null;
    onChange: (value: string | null) => void;
    errorMessage?: string;
    id: string;
    disabled?: boolean;
}

// Helper function to group timezones by region
const groupTimezones = () => {
    const timezones = moment.tz.names();
    const grouped: { [key: string]: string[] } = {};

    timezones.forEach((timezone) => {
        const [region] = timezone.split("/");
        if (!grouped[region]) {
            grouped[region] = [];
        }
        grouped[region].push(timezone);
    });

    return grouped;
};

const TimezoneInput: FC<TimezoneInputProps> = ({
    label,
    value,
    onChange,
    errorMessage = "",
    id,
    disabled = false,
}) => {
    const [groupedTimezones, setGroupedTimezones] = useState<{ [key: string]: string[] } | null>(null);

    useEffect(() => {
        setGroupedTimezones(groupTimezones());
    }, []);

    const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (!disabled) {
            const selectedValue = event.target.value;
            onChange(selectedValue || null);
        }
    };

    const handleReset = () => {
        if (!disabled) {
            onChange(null);
        }
    };

    if (!groupedTimezones) {
        return <div>Loading timezones...</div>;
    }

    return (
        <div className={styles.container}>
            {label && <label htmlFor={id} className={styles.label}>{label}</label>}
            <div className={styles.selectWrapper}>
                <select
                    id={id}
                    className={styles.timezoneSelect}
                    value={value || ""}
                    onChange={handleChange}
                    disabled={disabled}
                >
                    <option value="" disabled>
                        Select a timezone
                    </option>
                    {Object.entries(groupedTimezones).map(([region, timezones]) => (
                        <optgroup key={region} label={region}>
                            {timezones.map((tz) => (
                                <option key={tz} value={tz}>
                                    {tz}
                                </option>
                            ))}
                        </optgroup>
                    ))}
                </select>
                {value && (
                    <button
                        type="button"
                        onClick={handleReset}
                        className={styles.resetButton}
                        aria-label="Reset timezone input"
                        disabled={disabled}
                    >
                        &#x2715;
                    </button>
                )}
            </div>
            {errorMessage && (
                <span id={`${id}-error`} className={styles.errorMessage}>
                    {errorMessage}
                </span>
            )}
        </div>
    );
};

export default TimezoneInput;